// Load Dependencies
import React, {Component} from "react";
import * as Yup from "yup";

import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";

import "./ServerCreate.component.scss";
import Form from "../../../../../utils/forms/components/Form";
import {FormGridField} from "../../../../../utils/forms/components/FormGrid";

interface ServerCreateState {
    creatingServer: any;
    isValid: boolean;
    serverVersion: string;
}

interface ServerCreateProps {
    onClose?: (...args: any[]) => any;
    onConfirm?: (...args: any[]) => any;
    coreVersions: any[];
    hall: any;
    loadingHall: boolean;
    loading: boolean;
}

const nameSchema = Yup.string()
    .required("Field is required.")
    .max(20, "Max. 20 characters.")
    .matches(new RegExp(/^[\w\-\ \.]+$/), `Special characters not supported.`)
    .matches(
        new RegExp(/^(?!\.)([\w\-\ \.]?)+$/),
        `Should not start with a '.'`
    );

const nameForm: FormGridField[] = [
    {
        name: "name",
        fieldType: "input",
        fieldOptions: {
            id: "name",
            label: "Server Name",
            placeholder: "Server Name",
            inputType: "text"
        },

        validation: nameSchema,
        columnClass: "col-12",
        formSpacing: false
    }
];

export default class ServerCreate extends Component<
    ServerCreateProps,
    ServerCreateState
> {
    protocolSelect: React.RefObject<Components.SelectMenu>;
    cashNameSelect: React.RefObject<Components.SelectMenu>;
    serverVersionSelect: React.RefObject<Components.SelectMenu>;

    constructor(props) {
        super(props);

        this.serverVersionSelect = React.createRef();

        this.state = {
            creatingServer: {
                type: 1,
                name: null,
                is_egames: false
            },
            isValid: false,
            serverVersion: undefined
        };
    }

    checkValidity = server => {
        return (
            server !== undefined &&
            server.type !== undefined &&
            server.type !== null &&
            server.serverVersion !== undefined &&
            server.serverVersion !== null &&
            server.name !== undefined &&
            server.name !== null &&
            server.name !== "" &&
            nameSchema.isValidSync(server.name) &&
            server.name.length <= 50
        );
    };

    onServerTypeChange = serverType => {
        const newFormValues = {
            ...this.state.creatingServer,
            type: serverType[0]
        };

        this.setState({
            creatingServer: newFormValues,
            isValid: this.checkValidity(newFormValues)
        });
    };

    onServerVersionChange = version => {
        const isEGamesVisible = this.isEGamesSubsidiary() &&
            this.isEGamesSupportedVersion(version);
        const wasEGamesVisible = this.isEGamesSubsidiary() &&
            this.isEGamesSupportedVersion(
                this.state.creatingServer.serverVersion
            );
        const newFormValues = {
            ...this.state.creatingServer,
            serverVersion: version,
            is_egames: isEGamesVisible
                ? wasEGamesVisible
                    ? this.state.creatingServer.is_egames
                    : true
                : false
        };
        this.setState({
            creatingServer: newFormValues,
            isValid: this.checkValidity(newFormValues)
        });
    };

    onChangeName = e => {
        const newFormValues = {
            ...this.state.creatingServer,
            name: e.values.name
        };

        this.setState({
            creatingServer: newFormValues,
            isValid: this.checkValidity(newFormValues)
        });
    };

    onEGamesChange = () => {
        const newFormValues = {
            ...this.state.creatingServer,
            is_egames: !this.state.creatingServer.is_egames
        };

        this.setState({
            creatingServer: newFormValues,
            isValid: this.checkValidity(newFormValues)
        });
    };

    isEGamesSubsidiary = () => {
        return ["NTT-PH", "D2R-PH"].includes(
            this.props.hall.subsidiary.description
        );
    };

    isEGamesSupportedVersion = version => {
        if (!version) return false;

        const versionParts = version.split(".").map(part => parseInt(part, 10));
        const major = versionParts[0];
        const minor = versionParts[1];

        return major > 2 || (major === 2 && minor >= 9);
    };

    submitServer = () => {
        if (this.state.isValid) {
            this.props.onConfirm(this.state.creatingServer);
        }
    };

    cleanBeforeClose = () => {
        this.setState({
            creatingServer: {
                type: 1,
                name: "",
                is_egames: false
            },
            isValid: false,
            serverVersion: undefined
        });
        this.props.onClose();
    };

    render = () => {
        if (this.props.loadingHall) {
            return null;
        } else {
            let types = [
                {
                    value: 1,
                    label: "Local"
                }
            ];

            if (this.props.hall.subsidiary.description === "FBM-PH") {
                types = [
                    {
                        value: 1,
                        label: "Local"
                    },
                    {
                        value: 2,
                        label: "Global"
                    },
                    {
                        value: 3,
                        label: "Hybrid"
                    }
                ];
            }

            const showEGamesSwitch =
                this.isEGamesSubsidiary() &&
                this.isEGamesSupportedVersion(
                    this.state.creatingServer.serverVersion
                );

            let coreVersions = []
            if (this.props.coreVersions) coreVersions = [...this.props.coreVersions].sort((a, b) => {
                return a.order - b.order
            }).reverse().filter(v=> v.active);

            return (
                <div id="create-server">
                    <div id="name" className="row">
                        <Form
                            fields={nameForm}
                            initialValues={{
                                name: ""
                            }}
                            onChange={this.onChangeName}
                        />
                    </div>
                    <div id="server-type" className="row">
                        <div className="col-12">
                            <div className="label-tooltip">
                                <label className="label">Type</label>
                            </div>
                            <div className="input-group">
                                <Components.ButtonSelector
                                    items={types}
                                    exclusive
                                    value={[this.state.creatingServer.type]}
                                    columnClass="col-xs-3"
                                    onChange={this.onServerTypeChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div id="schema" className="row">
                        <div className="col-12">
                            <div className="label-tooltip">
                                <label className="label">
                                    System Server Version
                                </label>
                            </div>
                            <div className="input-group">
                                <Components.SelectMenu
                                    ref={this.serverVersionSelect}
                                    noValueLabel="Choose a System Server Version..."
                                    items={
                                        coreVersions.map(
                                            ver => {
                                                return {
                                                    value: ver.version,
                                                    label: ver.version
                                                };
                                            }
                                        )
                                    }
                                    disabled={!this.props.coreVersions.length}
                                    onChange={this.onServerVersionChange}
                                />
                            </div>
                        </div>
                    </div>
                    {showEGamesSwitch && (
                        <div id="egames" className="row">
                            <div className="col-12">
                                <div className="label-tooltip">
                                    <label className="label">eGames</label>
                                </div>
                                <div className="input-group">
                                    <Components.Switch
                                        onClick={this.onEGamesChange}
                                        on={this.state.creatingServer.is_egames}
                                        enabled
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-12">
                            <button
                                className={`red-btn create-btn ${
                                    this.props.loading ? "loading" : ""
                                }`}
                                disabled={
                                    !this.state.isValid || this.props.loading
                                }
                                onClick={this.submitServer}
                            >
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
    };
}
